// hooks/useHotelService.ts
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  CreateHotelServiceRequest,
  UpdateHotelServiceRequest,
  HotelServicesFilters,
  HotelServicesResponse,
  HotelService,
  HotelServiceProviderType,
  PartnerServiceFilterOptions,
} from "@/types/hotelService";
import { hotelService } from "@/services/hotel-service-service";

const hotelServiceKeys = {
  all: ["hotelServices"] as const,

  // All list queries
  lists: () => [...hotelServiceKeys.all, "list"] as const,

  // Specific list query with filters and pagination
  list: (
    hotelId: string,
    page: number,
    limit: number,
    filters?: HotelServicesFilters,
  ) => [...hotelServiceKeys.lists(), hotelId, page, limit, filters] as const,

  // All detail queries
  details: () => [...hotelServiceKeys.all, "detail"] as const,

  // Specific service detail
  detail: (hotelId: string, id: string) =>
    [...hotelServiceKeys.details(), hotelId, id] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;
const PARTNER_SERVICE_FILTER_OPTIONS_STALE_TIME = 5 * 60 * 1000;

const invalidateHotelServiceRelatedQueries = (
  queryClient: QueryClient,
  hotelId: string,
) => {
  queryClient.invalidateQueries({ queryKey: hotelServiceKeys.all });
  queryClient.invalidateQueries({ queryKey: ["services", hotelId] });
  queryClient.invalidateQueries({ queryKey: ["featured-services", hotelId] });
  queryClient.invalidateQueries({ queryKey: ["checkin-services", hotelId] });
  queryClient.invalidateQueries({ queryKey: ["checkout-services", hotelId] });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
};

export const useAllServices = (
  hotelId: string,
  providerTypes?: HotelServiceProviderType[],
  enabled = true,
) => {
  const {
    data: services,
    isLoading,
    error,
  } = useQuery<HotelServicesResponse, Error>({
    queryKey: ["services", hotelId, providerTypes],
    queryFn: () => hotelService.getAllServices(hotelId, providerTypes),
    enabled: enabled && !!hotelId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: services,
    isLoading,
    error,
  };
};

// Get list of services
export const useHotelServices = (
  hotelId: string,
  page?: number,
  limit?: number,
  filters?: HotelServicesFilters,
) => {
  return useQuery<HotelServicesResponse>({
    queryKey: hotelServiceKeys.list(hotelId, page ?? 1, limit ?? 10, filters),
    queryFn: () => hotelService.getServices(hotelId, page, limit, filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const usePartnerServiceFilterOptions = (
  hotelId: string,
  enabled = true,
) => {
  return useQuery<PartnerServiceFilterOptions>({
    queryKey: ["partnerServiceFilterOptions", hotelId],
    queryFn: () => hotelService.getPartnerServiceFilterOptions(hotelId),
    enabled: enabled && !!hotelId,
    staleTime: PARTNER_SERVICE_FILTER_OPTIONS_STALE_TIME,
  });
};

export const useFeaturedServices = (hotelId: string) => {
  return useQuery<HotelService[]>({
    queryKey: ["featured-services", hotelId],
    queryFn: () => hotelService.getFeaturedServices(hotelId),
    enabled: !!hotelId,
    staleTime: 5 * 60 * 1000,
  });
};

// Get single service
export const useHotelService = (id: string, hotelId: string) => {
  return useQuery<HotelService>({
    queryKey: hotelServiceKeys.detail(hotelId, id),
    queryFn: () => hotelService.getServiceById(id, hotelId),
    enabled: !!id && !!hotelId,
    staleTime: 5 * 60 * 1000,
  });
};

// Create service
export const useCreateHotelService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateHotelServiceRequest) =>
      hotelService.createService(request),
    onSuccess: (_, variables) => {
      invalidateHotelServiceRelatedQueries(queryClient, variables.hotelId);
    },
  });
};

// Update service
export const useUpdateHotelService = (id: string, hotelId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateHotelServiceRequest) =>
      hotelService.updateService(id, hotelId, request),
    onSuccess: (updatedService) => {
      queryClient.setQueryData(
        hotelServiceKeys.detail(hotelId, updatedService.id),
        updatedService,
      );
      invalidateHotelServiceRelatedQueries(queryClient, hotelId);
    },
  });
};

// Delete service
export const useDeleteHotelService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, hotelId }: { id: string; hotelId: string }) =>
      hotelService.deleteService(id, hotelId),
    onSuccess: (_, { id, hotelId }) => {
      queryClient.removeQueries({
        queryKey: hotelServiceKeys.detail(hotelId, id),
      });
      invalidateHotelServiceRelatedQueries(queryClient, hotelId);
    },
  });
};

export const useActivateHotelService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, hotelId }: { id: string; hotelId: string }) =>
      hotelService.activateService(id, hotelId),
    onSuccess: (_, variables) => {
      invalidateHotelServiceRelatedQueries(queryClient, variables.hotelId);
    },
  });
};

export const useDeactivateHotelService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, hotelId }: { id: string; hotelId: string }) =>
      hotelService.deactivateService(id, hotelId),
    onSuccess: (_, variables) => {
      invalidateHotelServiceRelatedQueries(queryClient, variables.hotelId);
    },
  });
};

export const useSetHotelServiceFeatured = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      hotelId,
      isRecommended,
    }: {
      id: string;
      hotelId: string;
      isRecommended: boolean;
    }) => hotelService.setServiceFeatured(id, hotelId, isRecommended),
    onSuccess: (_, { id, hotelId }) => {
      queryClient.invalidateQueries({
        queryKey: hotelServiceKeys.detail(hotelId, id),
      });
      invalidateHotelServiceRelatedQueries(queryClient, hotelId);
    },
  });
};

// Get checkin services
export const useCheckinServices = (hotelId: string) => {
  return useQuery<HotelService[]>({
    queryKey: ["checkin-services", hotelId],
    queryFn: () => hotelService.getCheckinServices(hotelId),
    enabled: !!hotelId,
    staleTime: 5 * 60 * 1000,
  });
};

// Get checkout services
export const useCheckoutServices = (hotelId: string) => {
  return useQuery<HotelService[]>({
    queryKey: ["checkout-services", hotelId],
    queryFn: () => hotelService.getCheckoutServices(hotelId),
    enabled: !!hotelId,
    staleTime: 5 * 60 * 1000,
  });
};
